// @givmo/app-charity — the charity console's ONE assistant control, in the console's assistant slot.
//
// A mount, not a fork (D-EP-44): everything here is DATA for `@givmo/assistant-panel`'s
// `ConsoleAssistant` — the charity as the organization the consent instrument binds, the console's
// address, its reads, and the scopes it declares (`assistant-scopes.ts`). The control renders only on a
// page a scope covers; on every other page the cluster carries no assistant markup at all.
//
// `registerCharityAssistant()` runs once at boot: it registers this component while the build
// declares any scope, and `null` otherwise, so a build with none draws exactly the cluster it drew
// before the slot was mounted here.

import { useMemo } from 'react';
import { useRouterState } from '@tanstack/react-router';
import {
  ConsoleAssistant,
  defaultAssistantVocab,
  type AssistantPanelConfig,
  type AssistantScope,
} from '@givmo/assistant-panel';
import { registerConsoleAssistant, setConsoleAssistantDock } from '@givmo/org-console';
import { useAuth } from '../../auth/auth-context';
import { useCharityConsole } from '../../console/charity-console-context';
import { useActiveOrg } from '../../org/active-org-context';
import { assistantStreamHeaders } from '../../lib/api-client';
import { PRIVACY_URL } from '../../lib/constants';
import { readApiEndpoint } from '../../lib/env';
import { charityAssistantScopes } from './assistant-scopes';

/** The test-id namespace — the same as every console's, one e2e helper. */
const PREFIX = 'assistant';

/** The organization-level string that turns the assistant on for a charity (`WRITE_ASSISTANT_CONSENT`). */
const MANAGE_ASSISTANT_CONSENT = 'charity_admin:edit';

function noticeStorageKey(uid: string | null): string | null {
  return uid === null || uid.length === 0 ? null : `givmo.assistant.notice.${uid}`;
}

export function CharityAssistantMount({ scopes }: { scopes: readonly AssistantScope[] }) {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const { user } = useAuth();
  const org = useActiveOrg();
  const { read } = useCharityConsole();
  const env = useMemo(() => readApiEndpoint(), []);

  const orgName = org.activeMembership?.name ?? null;
  const base: Omit<
    AssistantPanelConfig,
    'wireSurface' | 'pageWireSurface' | 'entryContextExtra' | 'activeSurfaceId' | 'threadMatch'
  > = useMemo(
    () => ({
      orgRef: org.isReady ? org.activeCharityId : null,
      orgName: orgName !== null && orgName.length > 0 ? orgName : null,
      // Advisory and permissive, as everywhere: only a settled set without the string is "can't".
      canAcceptForOrg:
        read.permissions === null || read.permissions.includes(MANAGE_ASSISTANT_CONSENT),
      surfaces: [],
      vocab: { ...defaultAssistantVocab(), consentScopeLine: null },
      privacyPolicyUrl: PRIVACY_URL,
      apiBaseUrl: env.apiBaseUrl,
      authHeaders: assistantStreamHeaders,
      env: env.deployEnv,
      // The console's disclosure is the organization's instrument, never the consumer greeting.
      renderServerOpeningMessage: false,
      noticeStorageKey: noticeStorageKey(user?.uid ?? null),
      testIdPrefix: PREFIX,
    }),
    [org.isReady, org.activeCharityId, orgName, read.permissions, env, user?.uid],
  );

  return (
    <ConsoleAssistant
      scopes={scopes}
      pathname={pathname}
      base={base}
      glyphOnPhone
      onDockChange={setConsoleAssistantDock}
    />
  );
}

/**
 * Register the charity console's assistant control into the slot — or, should this build declare no
 * scope, deliberately register nothing.
 */
export function registerCharityAssistant(): void {
  const scopes = charityAssistantScopes();
  function CharityAssistantEntry() {
    return <CharityAssistantMount scopes={scopes} />;
  }
  registerConsoleAssistant(scopes.length === 0 ? null : CharityAssistantEntry);
}
